/**
 * Worker Props — small held objects for ambient behaviors.
 * Coffee cup + book, attached near worker's right hand.
 * Hidden by default, toggled visible by worker state.
 */

import * as THREE from 'three';

/**
 * Create a small coffee cup prop (cylinder body + torus handle).
 * Returns a group with .visible = false by default.
 */
export function createCoffeeCupProp(): THREE.Group {
  const group = new THREE.Group();
  group.visible = false;

  const cupMat = new THREE.MeshStandardMaterial({ color: 0xdddddd }); // White cup
  const coffeeMat = new THREE.MeshStandardMaterial({ color: 0x4a2c14 }); // Dark brown

  // Cup body
  const cupGeo = new THREE.CylinderGeometry(0.035, 0.03, 0.06, 8);
  const cup = new THREE.Mesh(cupGeo, cupMat);
  cup.position.y = 0.03;
  group.add(cup);

  // Coffee surface (dark disc on top)
  const coffeeGeo = new THREE.CircleGeometry(0.032, 8);
  const coffee = new THREE.Mesh(coffeeGeo, coffeeMat);
  coffee.rotation.x = -Math.PI / 2;
  coffee.position.y = 0.058;
  group.add(coffee);

  // Handle (torus ring on side)
  const handleGeo = new THREE.TorusGeometry(0.018, 0.005, 6, 12);
  const handle = new THREE.Mesh(handleGeo, cupMat);
  handle.position.set(0.045, 0.03, 0);
  handle.rotation.y = Math.PI / 2;
  group.add(handle);

  return group;
}

/**
 * Create a small book prop (thin colored box).
 * Returns a group with .visible = false by default.
 */
export function createBookProp(): THREE.Group {
  const group = new THREE.Group();
  group.visible = false;

  // Randomize book color from a palette
  const bookColors = [0x2c5f8a, 0x8a2c3f, 0x3a8a2c, 0x8a6e2c, 0x5c2c8a];
  const color = bookColors[Math.floor(Math.random() * bookColors.length)];

  const bookMat = new THREE.MeshStandardMaterial({ color });
  const pageMat = new THREE.MeshStandardMaterial({ color: 0xf5f0e0 }); // Cream pages

  // Book cover
  const coverGeo = new THREE.BoxGeometry(0.05, 0.07, 0.015);
  const cover = new THREE.Mesh(coverGeo, bookMat);
  group.add(cover);

  // Pages (slightly smaller, lighter color)
  const pagesGeo = new THREE.BoxGeometry(0.045, 0.065, 0.012);
  const pages = new THREE.Mesh(pagesGeo, pageMat);
  pages.position.z = 0.001;
  group.add(pages);

  return group;
}
